import {
  CatalogStorefront,
  CatalogTreeRootInput,
  useProductCatalogBrandsListQuery,
  useProductCatalogByLocatorQuery,
  useProductCatalogDepartmentsQuery,
  useSeoRedirectByLocatorQuery,
} from '@graphql/generated';
import { useCallback, useMemo, useRef } from 'react';
import {
  CatalogDepartmentEntry,
  CatalogDepartmentCategoryEntry,
  CatalogDepartmentCategorySubcategoryEntry,
  CatalogDepartmentDictionaryEntry,
  CatalogDepartmentTreeRoot,
  CatalogDepartment,
  CatalogDepartmentCategory,
  CatalogDepartmentCategorySubcategory,
} from '../../state/Catalog';
import { useCatalogRoutePaths } from '@apps/registry/common/components/Catalog/Catalog.routes';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useRouteMatch } from '@react-router';
import { useTranslation } from '@shared/core';

type UseCatalogDepartmentsArgs = {
  selectedCategory: Maybe<Readonly<CatalogDepartmentDictionaryEntry>>;
};

const babyBrandPagePath = '/shop/baby/brands';

export type UseCatalogDepartmentsReturn = ReturnType<typeof useCatalogDepartments>;
export const useCatalogDepartments = (args: UseCatalogDepartmentsArgs) => {
  const { t } = useTranslation('catalogRegistry');
  const { allBrands, brands } = t('brands');

  const BRAND_DEPARTMENT = useMemo(
    (): CatalogDepartmentTreeRoot => ({
      children: [
        {
          id: 'all-brands',
          publicUrlPath: '/shop/brands',
          title: allBrands(),
          children: [],
        },
      ],
      id: '1',
      publicUrlPath: '/shop/brands',
      thumbnail: { url: 'https://withjoy.com/assets/public/shop/departmentIcons/10-12-2023/brands.svg?ver=2&opt=none' },
      title: brands(),
    }),
    [allBrands, brands],
  );

  const { currentStorefront, isBabyStorefront, catalogRoutes } = useCatalogRoutePaths();
  const categoryRouteMatch = useRouteMatch<{ eventHandle?: string; locator: string }>(catalogRoutes.catalogCategory.paths);
  const curationsRouteMatch = useRouteMatch<{ eventHandle?: string; locator: string }>(catalogRoutes.curationDetails.paths);

  const { data, loading, error } = useProductCatalogDepartmentsQuery({
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    variables: {
      storefront: currentStorefront,
      types: isBabyStorefront ? [CatalogTreeRootInput.default] : [CatalogTreeRootInput.curations, CatalogTreeRootInput.default],
    },
  });
  const { data: byLocatorData, loading: byLocatorLoading } = useProductCatalogByLocatorQuery({
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    skip: !categoryRouteMatch?.url,
    variables: {
      url: categoryRouteMatch?.url || '',
    },
  });

  const { data: redirectData } = useSeoRedirectByLocatorQuery({
    batchMode: 'fast',
    skip: !categoryRouteMatch?.url && !curationsRouteMatch?.url,
    variables: {
      url: categoryRouteMatch?.url || curationsRouteMatch?.url || '',
    },
  });

  const { data: brandList } = useProductCatalogBrandsListQuery({ variables: { args: { category: null } }, batchMode: 'fast', fetchPolicy: 'cache-first' });

  const { value: catalogBrandsDirectoryEnabled } = useFeatureValue('catalogBrandsDirectoryEnabled');

  const categoriesTreeRoot = useMemo(() => data?.productCatalogCategoryTreeRoot && { ...data?.productCatalogCategoryTreeRoot }, [data?.productCatalogCategoryTreeRoot]);
  const treeRootDepartments = [...(categoriesTreeRoot?.children || [])];

  if (categoriesTreeRoot?.children && catalogBrandsDirectoryEnabled === 'on') {
    const isBrandNotInArray = treeRootDepartments.every(item => item.id !== BRAND_DEPARTMENT.id);
    if (isBrandNotInArray) {
      treeRootDepartments.unshift({ ...BRAND_DEPARTMENT, ...(currentStorefront === CatalogStorefront.baby && { publicUrlPath: babyBrandPagePath }) });
      const populartoresIndex = treeRootDepartments.findIndex(dep => dep.title?.toLowerCase() === 'popular stores');
      if (populartoresIndex !== -1) {
        const [popularStoresDept] = treeRootDepartments.splice(populartoresIndex, 1);
        treeRootDepartments.unshift(popularStoresDept);
      }
      categoriesTreeRoot.children = treeRootDepartments;
    }
  }
  const hasLoadedOnceRef = useRef(!!treeRootDepartments);

  if (!loading || !byLocatorLoading || data || byLocatorData) {
    hasLoadedOnceRef.current = hasLoadedOnceRef.current || !!treeRootDepartments;
  }

  const departments: ReadonlyArray<CatalogDepartmentEntry & { __IS_CURATIONS__?: boolean; __IS_POPULAR_STORES__?: boolean; __IS_BRANDS__?: boolean }> = useMemo(() => {
    return categoriesTreeRoot?.children
      ? categoriesTreeRoot.children.map(x => ({
          ...x,
          lineageCsp: [categoriesTreeRoot],
          __IS_CURATIONS__: x.title === 'Curations' || x.title === 'Collections',
          __IS_POPULAR_STORES__: x.title === 'Popular Stores',
          __IS_BRANDS__: x.title === 'Brands',
        }))
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesTreeRoot?.children]);

  const catalogCategoryDictionary = useMemo(() => {
    const categoriesTreeRoot = data?.productCatalogCategoryTreeRoot && { ...data.productCatalogCategoryTreeRoot };
    if (!categoriesTreeRoot || !categoriesTreeRoot?.children.length) {
      return null;
    }
    const treeRootDepartments = [...categoriesTreeRoot.children];

    treeRootDepartments.unshift({ ...BRAND_DEPARTMENT, ...(currentStorefront === CatalogStorefront.baby && { publicUrlPath: babyBrandPagePath }) });
    const populartoresIndex = treeRootDepartments.findIndex(dep => dep.title?.toLowerCase() === 'popular stores');
    if (populartoresIndex !== -1) {
      const [popularStoresDept] = treeRootDepartments.splice(populartoresIndex, 1);
      treeRootDepartments.unshift(popularStoresDept);
    }
    categoriesTreeRoot.children = treeRootDepartments;

    const dictionary: Record<string, CatalogDepartmentDictionaryEntry> = {};
    categoriesTreeRoot.children.forEach(department => {
      const isCurationDepartment = department.title?.toLowerCase() === 'curations';
      const isPopularStoresDepartment = department.title?.toLowerCase() === 'popular stores';
      const departmentEntry: CatalogDepartmentEntry = {
        ...department,
        lineageCsp: [categoriesTreeRoot],
        isCurationCategory: isCurationDepartment,
        isPopularStoreCategory: isPopularStoresDepartment,
      };
      dictionary[department.publicUrlPath] = departmentEntry;
      dictionary[department.id] = departmentEntry;

      department.children.forEach(category => {
        const categoryEntry: CatalogDepartmentCategoryEntry = {
          ...category,
          lineageCsp: [categoriesTreeRoot, department],
          isCurationCategory: isCurationDepartment,
          isPopularStoreCategory: isPopularStoresDepartment,
        };
        dictionary[category.publicUrlPath] = categoryEntry;
        dictionary[category.id] = categoryEntry;

        category.children.forEach(subcategory => {
          const subCategoryEntry: CatalogDepartmentCategorySubcategoryEntry = { ...subcategory, lineageCsp: [categoriesTreeRoot, department, category] };
          dictionary[subcategory.publicUrlPath] = subCategoryEntry;
          dictionary[subcategory.id] = subCategoryEntry;
        });
      });
      if (department.id === BRAND_DEPARTMENT.id) {
        brandList?.productCatalogBrandsDirectory.brands.forEach(item => {
          const subCategoryEntry: CatalogDepartmentCategorySubcategoryEntry = {
            ...item,
            title: item.name,
            __typename: 'ProductCatalogCategory',
            lineageCsp: [categoriesTreeRoot, department, { ...item, title: item.name, children: [], __typename: 'ProductCatalogCategory' }],
          };
          dictionary[item.publicUrlPath] = subCategoryEntry;
          dictionary[item.id] = subCategoryEntry;
        });
      }
    });
    return dictionary;
  }, [brandList, data, currentStorefront, BRAND_DEPARTMENT]);

  const catalogCategoryByLocatorEntry = useMemo(() => {
    const category = byLocatorData?.productCatalogCategoryByLocator && { ...byLocatorData?.productCatalogCategoryByLocator };
    if (!category || !category.children.length || !catalogCategoryDictionary || !catalogCategoryDictionary[category.publicUrlPath]) {
      return null;
    }

    const isCurationDepartment = category.title?.toLowerCase() === 'curations';
    const isPopularStoresDepartment = category.title?.toLowerCase() === 'popular stores';
    const entry: CatalogDepartmentDictionaryEntry = {
      ...category,
      lineageCsp: catalogCategoryDictionary[category.publicUrlPath].lineageCsp as [CatalogDepartmentTreeRoot],
      isCurationCategory: isCurationDepartment,
      isPopularStoreCategory: isPopularStoresDepartment,
    };

    return entry;
  }, [byLocatorData?.productCatalogCategoryByLocator, catalogCategoryDictionary]);

  const getDepartmentByIdOrPath = useCallback(
    (idOrPath: Maybe<string>) => {
      return (idOrPath && catalogCategoryDictionary?.[idOrPath]) || catalogCategoryByLocatorEntry || null;
    },
    [catalogCategoryDictionary, catalogCategoryByLocatorEntry],
  );

  const categoryHasChildren = useCallback(
    (
      category: Maybe<CatalogDepartment | CatalogDepartmentTreeRoot | CatalogDepartmentCategory | CatalogDepartmentCategorySubcategory>,
    ): category is CatalogDepartment | CatalogDepartmentTreeRoot | CatalogDepartmentCategory => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return !!(category as any)?.children;
    },
    [],
  );

  return {
    loading,
    hasLoadedOnce: hasLoadedOnceRef.current,
    redirectPath: redirectData?.productCatalogCategoryByLocator?.seoRedirectPath,
    departments,
    catalogCategoryDictionary,
    categoryHasChildren,
    getDepartmentByIdOrPath,
    error,
  } as const;
};
